import { FunctionComponent, useEffect, useState } from "react"
import NavigationControls from "../../../shared/navigationControls"
import styles from "./IwpSubPageNavigation.module.scss"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Dropdown from "../../../shared/dropDown"
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons"
import { useLocation, useNavigate } from "react-router-dom"
import {
    SubjectRole,
    WellIdentificationFieldsFragment,
} from "../../../../generated/graphql"
import {
    WELL_DETAILS_ABS_ROUTE,
    WELL_DETAILS_BASE_SLUG,
    WELL_NOTES_ABS_ROUTE,
} from "../../../.."
import { useViewerContext } from "../../../../context/ViewerContext"
import { SubNavDropDownI } from ".."

interface IwpSubPageNavigationI {
    wellIdentificationfragment: WellIdentificationFieldsFragment
    windowWidth: number
    mobileBreakPoint: number
    dropDownMenus: SubNavDropDownI
    setDropDownMenus: React.Dispatch<React.SetStateAction<SubNavDropDownI>>
}

const IwpSubPageNavigation: FunctionComponent<IwpSubPageNavigationI> = ({
    wellIdentificationfragment,
    windowWidth,
    mobileBreakPoint,
    dropDownMenus,
    setDropDownMenus,
}) => {
    const [mobileView, setMobileView] = useState(
        windowWidth > mobileBreakPoint ? false : true
    )

    useEffect(() => {
        if (windowWidth > mobileBreakPoint) {
            setMobileView(false)
        }
        if (windowWidth < mobileBreakPoint) {
            setMobileView(true)
        }
    }, [windowWidth])

    if (mobileView) {
        return (
            <div className={styles.navControlsContainer}>
                <MobileView
                    wellIdentificationfragment={wellIdentificationfragment}
                    dropDownMenus={dropDownMenus}
                    setDropDownMenus={setDropDownMenus}
                />
            </div>
        )
    } else {
        return (
            <div className={styles.navControlsContainer}>
                <DesktopView
                    wellIdentificationfragment={wellIdentificationfragment}
                />
            </div>
        )
    }
}

export default IwpSubPageNavigation

interface DesktopI {
    wellIdentificationfragment: WellIdentificationFieldsFragment
}

const getSelectedSubNavControl = (urlPath: string) => {
    const initialPathSegment = urlPath.split("/")[1]

    return {
        wellDetails:
            initialPathSegment === WELL_DETAILS_BASE_SLUG ? true : false,
        wellAttributes: initialPathSegment === "wellAttributes" ? true : false,
        wellNotes: initialPathSegment === "wellNotes" ? true : false,
    }
}

const DesktopView: FunctionComponent<DesktopI> = ({
    wellIdentificationfragment,
}) => {
    // hooks
    const navigate = useNavigate()

    const { pathname } = useLocation()
    const currentNavSelection = getSelectedSubNavControl(pathname)

    return (
        <>
            <div className={styles.navControlOverview}>
                <NavigationControls
                    usage="sub-nav"
                    condition={
                        currentNavSelection.wellDetails ? "selected" : "default"
                    }
                    disabled={false}
                    handleClick={(e) => {
                        e.stopPropagation()
                        navigate(
                            WELL_DETAILS_ABS_ROUTE(
                                wellIdentificationfragment.id
                            )
                        )
                    }}
                >
                    Overview
                </NavigationControls>
            </div>
            <div className={styles.navControlOverview}>
                <NavigationControls
                    usage="sub-nav"
                    condition={
                        currentNavSelection.wellAttributes
                            ? "selected"
                            : "default"
                    }
                    disabled={false}
                    handleClick={(e) => {
                        e.stopPropagation()
                        navigate(
                            `/wellAttributes/${wellIdentificationfragment.id}`
                        )
                    }}
                >
                    Attributes
                </NavigationControls>
            </div>
            <div>
                <NavigationControls
                    usage="sub-nav"
                    condition={
                        currentNavSelection.wellNotes ? "selected" : "default"
                    }
                    disabled={false}
                    handleClick={(e) => {
                        e.stopPropagation()
                        navigate(
                            `${WELL_NOTES_ABS_ROUTE(
                                wellIdentificationfragment.id
                            )}`
                        )
                    }}
                >
                    Notes
                </NavigationControls>
            </div>
        </>
    )
}

interface MobileViewI {
    wellIdentificationfragment: WellIdentificationFieldsFragment
    dropDownMenus: SubNavDropDownI
    setDropDownMenus: React.Dispatch<React.SetStateAction<SubNavDropDownI>>
}

const MobileView: FunctionComponent<MobileViewI> = ({
    wellIdentificationfragment,
    dropDownMenus,
    setDropDownMenus,
}) => {
    const navigate = useNavigate()
    const dropDownMenuId = "mobileMenu"

    /** PPO admin check */
    const viewer = useViewerContext()
    const viewerRole = viewer.getViewer().role
    const dropDownOptions = [
        { value: "overview", label: "Overview" },
        { value: "attributes", label: "Attributes" },
    ]

    if (viewerRole === SubjectRole.PpoAdmin) {
        dropDownOptions.push({ value: "notes", label: "Notes" })
    }

    return (
        <>
            <Dropdown
                options={dropDownOptions}
                dropDownButtonClass="dropDown-button-class"
                dropDownOptionsContainerClass="dropDown-options-container"
                dropDownOptionClass="dropDown-option"
                positionClass={"iwp-sub-nav-mobile"}
                menuOpen={dropDownMenus.subPageMenu}
                handleDropDownMenuClick={(e) => {
                    e.stopPropagation()
                    const updatedDropDownMenus = {
                        ...dropDownMenus,
                        subPageMenu: !dropDownMenus.subPageMenu,
                        configurationMenu: false,
                    }
                    setDropDownMenus(updatedDropDownMenus)
                }}
                handleDropDownOptionClick={(e) => {
                    e.stopPropagation()
                    const targetId = (e.currentTarget as HTMLElement).id
                    switch (targetId) {
                        case `${dropDownMenuId}_overview`:
                            navigate(
                                WELL_DETAILS_ABS_ROUTE(
                                    wellIdentificationfragment.id
                                )
                            )
                            break
                        case `${dropDownMenuId}_attributes`:
                            navigate(
                                `/wellAttributes/${wellIdentificationfragment.id}`
                            )
                            break
                        case `${dropDownMenuId}_notes`:
                            navigate(
                                `${WELL_NOTES_ABS_ROUTE(
                                    wellIdentificationfragment.id
                                )}`
                            )
                            break

                        default:
                            throw new Error(
                                "Unexpected Behavior in well control drop down function"
                            )
                    }
                    setDropDownMenus({
                        configurationMenu: false,
                        subPageMenu: false,
                    })
                }}
                id={dropDownMenuId}
            >
                <FontAwesomeIcon color="#1e88e5" icon={faChevronDown} />
            </Dropdown>
        </>
    )
}
